import { Overlay } from '@angular/cdk/overlay';
import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  ViewChild,
  inject,
} from '@angular/core';
import type { AbstractControl } from '@angular/forms';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import type { MatDatepickerInputEvent } from '@angular/material/datepicker';
import {
  MAT_DATEPICKER_SCROLL_STRATEGY,
  MatDatepicker,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { TrackingModule } from '@freelancer/tracking';
import { getDaysInMonth } from 'date-fns';
import { Subscription } from 'rxjs';
import { ButtonComponent } from '../button/button.component';
import {
  MAT_DATE_FNS_PROVIDERS,
  MAT_DATEFNS_DATE_FORMATS,
} from '../date-fns-date-adapter.service';
import {
  flCalendarV2,
  IconColor,
  IconModule,
  IconSize,
  provideFlIcons,
} from '../icon';
import { InputComponent, InputSize, InputType } from '../input';
import { LocalizedDateFns } from '../localized-date-fns.service';
import type { SelectItem } from '../select';
import { SelectSize } from '../select';
import { SelectComponent } from '../select/select.component';
import { ValidationErrorComponent } from '../validation-error';
import { SegmentedDateInputCalendarHeaderComponent } from './segmented-date-input-calendar-header.component';
import { SegmentedDateInputYearOrderSource } from './segmented-date-input-year-order.service';
import type {
  SegmentedDateInputField,
  SegmentedDateInputYearOrder,
} from './segmented-date-input.types';

const MAX_DAYS_IN_ANY_MONTH = 31;

const LEAP_REFERENCE_YEAR = 2000;
/** How far back the year dropdown reaches when no `minDate` is bound. */
const DEFAULT_YEAR_SPAN = 120;
/** Locales overwhelmingly lead with the day, so this is the pre-locale guess. */
const DEFAULT_FIELD_ORDER: readonly SegmentedDateInputField[] = [
  'day',
  'month',
  'year',
];
/** Hook for the width override in `styles/vendor/absolute-global-vendor.scss`. */
const CALENDAR_PANEL_CLASS = 'SegmentedDateInputCalendar';

/**
 * A date input split into separate day, month and year fields, with an
 * optional calendar as a secondary path.
 *
 * Bind `minDate` and `maxDate` wherever you can — they size the year dropdown
 * and bound the calendar.
 */
@Component({
  standalone: true,
  selector: 'fl-segmented-date-input',
  imports: [
    ButtonComponent,
    IconModule,
    InputComponent,
    MatDatepickerModule,
    ReactiveFormsModule,
    SelectComponent,
    TrackingModule,
    ValidationErrorComponent,
  ],
  template: `
    <div
      class="SegmentedDateInput"
      flTrackingSection="SegmentedDateInput"
      role="group"
      [attr.aria-label]="label"
      [attr.data-size]="size"
      (focusout)="handleFocusOut($event)"
    >
      <div class="SegmentedDateInput-fields">
        @for (field of fieldOrder; track field) {
          @switch (field) {
            @case ('day') {
              <fl-input
                class="SegmentedDateInput-day"
                flTrackingLabel="DayField"
                [attr.data-field]="'day'"
                [control]="dayControl"
                [displayValidation]="false"
                [id]="segmentId('day')"
                [keyboardType]="'numeric'"
                [label]="dayLabel"
                [maxLength]="2"
                [placeholder]="dayLabel"
                [size]="size"
                [type]="InputType.TEXT"
                (keydown)="handleSegmentKeydown($event, 'day')"
                (keyup)="handleDayKeyUp()"
                (paste)="handlePaste($event)"
              ></fl-input>
            }
            @case ('month') {
              <fl-select
                class="SegmentedDateInput-month"
                flTrackingLabel="MonthField"
                [attr.data-field]="'month'"
                [control]="monthControl"
                [id]="segmentId('month')"
                [label]="monthLabel"
                [options]="monthOptions"
                [placeholder]="monthLabel"
                [size]="selectSize"
                (keydown)="handleSegmentKeydown($event, 'month')"
              ></fl-select>
            }
            @case ('year') {
              <fl-select
                class="SegmentedDateInput-year"
                flTrackingLabel="YearField"
                [attr.data-field]="'year'"
                [control]="yearControl"
                [id]="segmentId('year')"
                [label]="yearLabel"
                [options]="yearOptions"
                [placeholder]="yearLabel"
                [size]="selectSize"
                (keydown)="handleSegmentKeydown($event, 'year')"
              ></fl-select>
            }
          }
        }
        @if (showCalendarPicker) {
          <fl-button
            class="SegmentedDateInput-calendarButton"
            flTrackingLabel="OpenCalendar"
            [disabled]="control.disabled"
            [label]="calendarLabel"
            (click)="handleCalendarClick()"
          >
            <fl-icon
              [color]="IconColor.INHERIT"
              [name]="'flCalendarV2'"
              [size]="IconSize.SMALL"
            ></fl-icon>
          </fl-button>
          <input
            aria-hidden="true"
            class="SegmentedDateInput-calendarAnchor"
            tabindex="-1"
            [formControl]="calendarControl"
            [matDatepicker]="picker"
            [max]="calendarMaxDate"
            [min]="calendarMinDate"
            (dateChange)="handleCalendarDateChange($event)"
          />
          <mat-datepicker
            #picker
            [calendarHeaderComponent]="calendarHeaderComponent"
            [panelClass]="calendarPanelClass"
            [startAt]="calendarStartAt"
          ></mat-datepicker>
        }
      </div>
      <fl-validation-error
        class="SegmentedDateInput-error"
        [control]="control"
        [id]="id + '-error'"
      ></fl-validation-error>
    </div>
  `,
  styleUrls: ['./segmented-date-input.component.scss'],
  providers: [
    ...MAT_DATE_FNS_PROVIDERS,
    provideFlIcons({ flCalendarV2 }),
    // Closes the calendar on scroll instead of repositioning it, which lags
    // behind the page.
    {
      provide: MAT_DATEPICKER_SCROLL_STRATEGY,
      deps: [Overlay],
      useFactory: (overlay: Overlay) => () => overlay.scrollStrategies.close(),
    },
    SegmentedDateInputYearOrderSource,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SegmentedDateInputComponent
  implements OnInit, OnChanges, OnDestroy
{
  private cd = inject(ChangeDetectorRef);
  private datefns = inject(LocalizedDateFns);
  private dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private yearOrderSource = inject(SegmentedDateInputYearOrderSource);

  IconColor = IconColor;
  IconSize = IconSize;
  InputType = InputType;

  /**
   * Month and year dropdowns in place of Material's period button and 24-year
   * grid. The input takes a component class, not an instance.
   */
  readonly calendarHeaderComponent = SegmentedDateInputCalendarHeaderComponent;
  readonly calendarPanelClass = CALENDAR_PANEL_CLASS;

  @Input() control: FormControl<Date | null>;
  @Input() id: string;
  @Input() size = InputSize.MID;
  /** Earliest selectable date. Sizes the year dropdown and bounds the calendar. */
  @Input() minDate?: Date;
  /** Latest selectable date. Sizes the year dropdown and bounds the calendar. */
  @Input() maxDate?: Date;
  @Input() yearOrder: SegmentedDateInputYearOrder = 'descending';
  /** Shows the calendar button as a secondary way to pick a date. */
  @Input() showCalendarPicker = true;
  /** Labels the group for screen readers, e.g. "Date of birth". */
  @Input() label?: string;
  @ViewChild('picker') private picker?: MatDatepicker<Date>;

  readonly dayControl = new FormControl<string>('', { nonNullable: true });
  readonly monthControl = new FormControl<number | null>(null, {
    nonNullable: true,
  });
  readonly yearControl = new FormControl<number | null>(null, {
    nonNullable: true,
  });
  /** Backs the hidden datepicker anchor only; not the source of truth. */
  readonly calendarControl = new FormControl<Date | null>(null, {
    nonNullable: true,
  });

  fieldOrder: readonly SegmentedDateInputField[] = DEFAULT_FIELD_ORDER;
  monthOptions: readonly SelectItem<number | null>[] = [];
  yearOptions: readonly SelectItem<number | null>[] = [];

  /**
   * The calendar's bounds. Fields rather than getters: they are bound to
   * `[min]`/`[max]`, and a getter minting a `Date` would look changed on every
   * check. {@link refreshBounds} is the only writer.
   */
  calendarMinDate: Date;
  calendarMaxDate: Date;

  readonly dayLabel = $localize`Day`;
  readonly monthLabel = $localize`Month`;
  readonly yearLabel = $localize`Year`;
  readonly calendarLabel = $localize`Open calendar`;

  /** Guards against the outbound write bouncing back in as an inbound sync. */
  private isSyncingOutwards = false;
  private readonly subscriptions = new Subscription();

  /**
   * Maps the one `size` callers pass onto `fl-select`'s own scale, so the
   * dropdowns match the day field without a second size input. `InputSize.AUTO`
   * has no `fl-select` equivalent, so those fall back to a fixed mid height.
   */
  get selectSize(): SelectSize {
    switch (this.size) {
      case InputSize.SMALL:
        return SelectSize.SMALL;
      case InputSize.LARGE:
        return SelectSize.LARGE;
      default:
        return SelectSize.MID;
    }
  }

  /**
   * Where the calendar opens. A partially-typed date is reported as an invalid
   * `Date`, which Material can't seat the year grid on, so anything other than a
   * real date falls back to the newest selectable one.
   */
  get calendarStartAt(): Date | null {
    const { value } = this.control;
    if (value && !Number.isNaN(value.getTime())) {
      return value;
    }
    return this.maxDate ?? null;
  }

  ngOnInit(): void {
    this.refreshBounds();
    this.datefns.dateFormat.then(dateFormat => {
      this.fieldOrder = deriveFieldOrder(dateFormat);
      this.cd.markForCheck();
    });

    // Months come from the date adapter, not `LocalizedDateFns.getLocalisedMonths()`.
    // That helper awaits the locale chunk, so a failed chunk load would leave this
    // dropdown permanently empty and the whole field unusable. The adapter formats
    // with `locale: undefined` until the chunk arrives, which degrades to English
    // instead of to nothing — and it's the same source the calendar header uses, so
    // the two month lists cannot disagree.
    this.monthOptions = [
      { value: null, displayText: '' },
      ...this.dateAdapter
        .getMonthNames('long')
        .map((month, index) => ({ value: index, displayText: month })),
    ];

    // ngOnChanges only fires for inputs that were actually bound, so build the
    // year list here too for callers that bind no bounds at all.
    this.yearOptions = this.buildYearOptions();
    this.yearOrderSource.yearOrder = this.yearOrder;
    this.writeFields(this.control.value);
    this.syncDisabledState();

    this.subscriptions.add(
      this.control.valueChanges.subscribe(value => {
        if (this.isSyncingOutwards) {
          return;
        }
        this.writeFields(value);
        this.cd.markForCheck();
      }),
    );

    // Fires when the bound control is disabled or enabled by its owner.
    this.subscriptions.add(
      this.control.statusChanges.subscribe(() => {
        this.syncDisabledState();
        this.cd.markForCheck();
      }),
    );

    // Typed as AbstractControl because the segments' value types differ, and a
    // union of FormControls collapses the overloads on `valueChanges`.
    const segments: readonly AbstractControl[] = [
      this.dayControl,
      this.monthControl,
      this.yearControl,
    ];
    segments.forEach(segment => {
      this.subscriptions.add(
        segment.valueChanges.subscribe(() => this.handleSegmentChange()),
      );
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.minDate || changes.maxDate || changes.yearOrder) {
      this.refreshBounds();
      this.yearOptions = this.buildYearOptions();
      // Keep the calendar header's year list the same way round as ours.
      this.yearOrderSource.yearOrder = this.yearOrder;
    }
  }

  /**
   * The first segment carries the bare `id`, so a caller's
   * `<fl-label [for]="id">` still targets something focusable — clicking the
   * label lands on whichever segment the locale puts first.
   */
  segmentId(field: SegmentedDateInputField): string {
    return field === this.fieldOrder[0] ? this.id : `${this.id}-${field}`;
  }

  handleCalendarClick(): void {
    this.picker?.open();
  }

  handleCalendarDateChange(event: MatDatepickerInputEvent<Date>): void {
    if (event.value) {
      this.writeFields(event.value);
      this.emitValue();
      this.markAsInteracted();
    }
  }

  handleDayKeyUp(): void {
    if (this.shouldAdvanceFromDay(this.dayControl.value)) {
      this.focusFieldAfter('day');
    }
  }

  /**
   * Backspace on an already-empty segment hands focus back to the previous one,
   * so deleting a date backwards doesn't need a reach for Shift+Tab. Every other
   * key — including Tab itself — is left alone.
   */
  handleSegmentKeydown(
    event: KeyboardEvent,
    field: SegmentedDateInputField,
  ): void {
    if (event.key !== 'Backspace' || !this.isSegmentEmpty(field)) {
      return;
    }
    this.focusFieldBefore(field);
  }

  /**
   * Pasting a whole date into the day segment fills all three. Parsing goes
   * through the date adapter, so it accepts exactly the formats the locale's
   * own date typing does; anything it can't read falls through as a normal
   * paste.
   */
  handlePaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text');
    if (!pasted) {
      return;
    }

    // `DateFnsDateAdapter.parse` ignores the format argument and matches against
    // the locale's own short date format — the same path fl-input's date typing
    // uses. `MAT_DATEFNS_DATE_FORMATS.parse.dateInput` is `undefined` for that
    // reason, so pass it through rather than inventing a format here.
    const parsed = this.dateAdapter.parse(
      pasted.trim(),
      MAT_DATEFNS_DATE_FORMATS.parse.dateInput,
    );
    if (!parsed || !this.dateAdapter.isValid(parsed)) {
      return;
    }

    event.preventDefault();
    this.writeFields(parsed);
    this.emitValue();
    this.markAsInteracted();
  }

  /**
   * Validation waits until focus leaves the whole component — moving between
   * segments shouldn't flash "Please enter a valid date" at a half-typed date.
   */
  handleFocusOut(event: FocusEvent): void {
    const nextTarget = event.relatedTarget;
    if (
      nextTarget instanceof Node &&
      this.elementRef.nativeElement.contains(nextTarget)
    ) {
      return;
    }
    this.clampDay();
    this.emitValue();
    this.markAsInteracted();
  }

  /**
   * Decides whether a keystroke has finished the day field, so focus can move
   * on to the next segment.
   *
   * A full two characters is unambiguous. A single digit above 3 is too: no
   * valid day starts with 4-9, so there is nothing left to type. `1`-`3` stay
   * put because they could still become `15` or `31` — which also means
   * backspacing `15` down to `1` doesn't yank focus away mid-correction.
   */
  private shouldAdvanceFromDay(value: string): boolean {
    if (value.length >= 2) {
      return true;
    }
    return value.length === 1 && Number.parseInt(value, 10) > 3;
  }

  private handleSegmentChange(): void {
    this.clampDay();
    this.emitValue();
  }

  /**
   * Trims the day down to the selected month's length. Only ever shrinks the
   * value — picking a leap year after February clamped 31 to 28 leaves the day
   * at 28 rather than silently moving it back up to 29.
   *
   * Callers emit afterwards; this only touches the day segment.
   */
  private clampDay(): void {
    const day = Number.parseInt(this.dayControl.value, 10);
    if (Number.isNaN(day)) {
      return;
    }

    const clamped = Math.min(Math.max(day, 1), this.daysInSelectedMonth());
    if (clamped !== day) {
      this.dayControl.setValue(String(clamped), { emitEvent: false });
    }
  }

  private daysInSelectedMonth(): number {
    const month = this.monthControl.value;
    if (month === null) {
      return MAX_DAYS_IN_ANY_MONTH;
    }
    const year = this.yearControl.value ?? LEAP_REFERENCE_YEAR;
    return getDaysInMonth(new Date(year, month, 1));
  }

  /**
   * Writes the segments out to the bound control.
   *
   * - nothing filled in yet -> `null`, so `required` reports it
   * - partially filled in -> an invalid `Date`, so `dateFormat` reports it
   * - all three filled in -> a real `Date` for `minDate` / `maxDate` to judge
   */
  private emitValue(): void {
    const date = this.buildDate();
    this.isSyncingOutwards = true;
    this.control.setValue(date);
    // Only hand the picker a real date — it would render an invalid one as
    // "Invalid Date" and fail its own bounds validation.
    this.calendarControl.setValue(
      date && !Number.isNaN(date.getTime()) ? date : null,
      { emitEvent: false },
    );
    this.isSyncingOutwards = false;
  }

  private buildDate(): Date | null {
    const rawDay = this.dayControl.value.trim();
    const month = this.monthControl.value;
    const year = this.yearControl.value;

    if (rawDay === '' && month === null && year === null) {
      return null;
    }

    const day = Number.parseInt(rawDay, 10);
    if (Number.isNaN(day) || month === null || year === null) {
      return new Date(NaN);
    }

    return new Date(year, month, day);
  }

  private writeFields(value: Date | null): void {
    if (value === null) {
      // A reset clears everything.
      this.dayControl.setValue('', { emitEvent: false });
      this.monthControl.setValue(null, { emitEvent: false });
      this.yearControl.setValue(null, { emitEvent: false });
      this.calendarControl.setValue(null, { emitEvent: false });
      return;
    }

    if (Number.isNaN(value.getTime())) {
      // An invalid date is how we report a half-typed one, so there's nothing
      // to write back — leave the segments as the user left them.
      this.calendarControl.setValue(null, { emitEvent: false });
      return;
    }

    this.dayControl.setValue(String(value.getDate()), { emitEvent: false });
    this.monthControl.setValue(value.getMonth(), { emitEvent: false });
    this.yearControl.setValue(value.getFullYear(), { emitEvent: false });
    this.calendarControl.setValue(value, { emitEvent: false });
  }

  private syncDisabledState(): void {
    const segments = [this.dayControl, this.monthControl, this.yearControl];
    segments.forEach(segment => {
      if (this.control.disabled) {
        segment.disable({ emitEvent: false });
      } else {
        segment.enable({ emitEvent: false });
      }
    });
  }

  private markAsInteracted(): void {
    this.control.markAsTouched();
    this.control.markAsDirty();
  }

  private isSegmentEmpty(field: SegmentedDateInputField): boolean {
    switch (field) {
      case 'day':
        return this.dayControl.value === '';
      case 'month':
        return this.monthControl.value === null;
      default:
        return this.yearControl.value === null;
    }
  }

  private focusFieldAfter(field: SegmentedDateInputField): void {
    this.focusField(this.fieldOrder[this.fieldOrder.indexOf(field) + 1]);
  }

  private focusFieldBefore(field: SegmentedDateInputField): void {
    const index = this.fieldOrder.indexOf(field);
    if (index <= 0) {
      return;
    }
    this.focusField(this.fieldOrder[index - 1]);
  }

  private focusField(field: SegmentedDateInputField | undefined): void {
    if (!field) {
      return;
    }
    this.elementRef.nativeElement
      .querySelector<HTMLElement>(
        `[data-field="${field}"] input, [data-field="${field}"] select`,
      )
      ?.focus();
  }

  /**
   * Resolves `minDate` / `maxDate` against a fallback of the last
   * {@link DEFAULT_YEAR_SPAN} years, so the dropdown, calendar and native input
   * can't offer different years.
   */
  private refreshBounds(): void {
    const thisYear = new Date().getFullYear();
    this.calendarMinDate =
      this.minDate ?? new Date(thisYear - DEFAULT_YEAR_SPAN, 0, 1);
    this.calendarMaxDate = this.maxDate ?? new Date(thisYear, 11, 31);
  }

  private buildYearOptions(): readonly SelectItem<number | null>[] {
    const firstYear = this.calendarMinDate.getFullYear();
    const lastYear = this.calendarMaxDate.getFullYear();

    const years: number[] = [];
    for (let year = firstYear; year <= lastYear; year++) {
      years.push(year);
    }
    if (this.yearOrder === 'descending') {
      years.reverse();
    }

    return [
      { value: null, displayText: '' },
      ...years.map(year => ({ value: year, displayText: String(year) })),
    ];
  }
}

/**
 * Reads the segment order out of a date-fns format string, so `MM/dd/yyyy`
 * locales render month-first without us keeping our own locale table.
 */
function deriveFieldOrder(
  dateFormat: string,
): readonly SegmentedDateInputField[] {
  const positions: readonly (readonly [SegmentedDateInputField, number])[] = [
    ['day', dateFormat.indexOf('d')],
    ['month', dateFormat.indexOf('M')],
    ['year', dateFormat.indexOf('y')],
  ];

  if (positions.some(([, index]) => index < 0)) {
    return DEFAULT_FIELD_ORDER;
  }

  return positions
    .slice()
    .sort(([, first], [, second]) => first - second)
    .map(([field]) => field);
}
